import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { StatusFormValues, parseDecimal } from "../config/statusFormConfig";
import { toLocalDateTime } from "./dateTime";
import { toTrackingHierarchy } from "./trackingHierarchy";
import { ProgressNode, TrackingContent, XapiActivityState } from "../types";

interface SaveTrackingArgs {
  corporateId: string | number;
  userId: string | number;
  node: ProgressNode;
  content: TrackingContent;
  values: StatusFormValues;
  activityStates?: XapiActivityState[];
}

const toText = (value: unknown): string | null =>
  value === undefined || value === null ? null : String(value);

const toScore = (value?: string | null): number | undefined =>
  parseDecimal(value) ?? undefined;

export const getTrackingInitialValues = (
  content: TrackingContent,
): StatusFormValues => {
  const { raw } = content;
  return {
    currentStatus: toText(content.status),
    newStatus: null,
    lessonLocationValue: toText(raw.location),
    suspendDateValue: toText(raw.suspendData),
    completionDate: toText(raw.enrollment?.completionDateTime),
    score: toText(raw.score),
    reason: null,
  };
};

const hierarchy = (
  corporateId: string | number,
  userId: string | number,
  node: ProgressNode,
) => ({
  corporateId: Number(corporateId),
  userId: Number(userId),
  activityId: node.activityId,
  ...toTrackingHierarchy(node),
});

const toDateTime = (value?: string | null): string | undefined =>
  value ? toLocalDateTime(value) : undefined;

export const saveTracking = ({
  corporateId,
  userId,
  node,
  content,
  values,
  activityStates,
}: SaveTrackingArgs) => {
  const base = {
    ...hierarchy(corporateId, userId, node),
    completionDateTime: toDateTime(values.completionDate),
    score: toScore(values.score),
    reason: values.reason ?? "",
  };

  if (content.kind === "scorm") {
    const url =
      content.scormType === "SCORM_12"
        ? ServicesURL.scormForceSco12
        : ServicesURL.scormForceSco2004;
    return fetcher<any>(url, MethodType.POST, {
      ...base,
      scoDataManagerId: content.raw.dataManagerId,
      ...(values.newStatus ? { status: values.newStatus } : {}),
      location: values.lessonLocationValue ?? undefined,
      suspendData: values.suspendDateValue ?? undefined,
    });
  }

  return fetcher<any>(ServicesURL.xapiForce, MethodType.POST, {
    ...base,
    ...(values.newStatus ? { verb: values.newStatus } : {}),
    ...(activityStates && activityStates.length > 0 ? { activityStates } : {}),
  });
};
